// Feb 28, 2026 — Global LLM Guard modal listener.
//
// Backend's enforce_llm_guard() returns HTTP 402 with detail.code='LLM_GUARD'
// whenever a creator triggers ANY LLM surface (workbench, refinery, AI
// buddy, public-Q on their own silo, etc.) without a BYOK key AND with an
// empty voucher bucket for that provider. The axios response interceptor
// (see lib/api.js) re-emits these as a `llm-guard-blocked` window event.
//
// This component listens for that event and pops a friendly teach-modal
// with two CTAs: top up the provider bucket OR add your own BYOK key.
import { useEffect, useState } from "react";
import { X, Plus, Key, ArrowSquareOut, ShieldCheck } from "@phosphor-icons/react";
import VoucherTopupModal from "@/components/VoucherTopupModal";
import { useNavigate } from "react-router-dom";
import { getUser } from "@/lib/api";

const PROVIDER_LABEL = { gemini: "Gemini", anthropic: "Anthropic", openai: "OpenAI" };

export default function LLMGuardModal() {
  const [event, setEvent] = useState(null);
  const [topupOpen, setTopupOpen] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    const handler = (e) => {
      try {
        // May 28, 2026 — This teach-modal is for CREATORS only (top up
        // YOUR bucket / add YOUR own key). Anonymous visitors and fans
        // on a public Q&A surface must NOT see it — they'd be confused.
        // PublicSilo.jsx renders its own friendly "owner is still
        // building their silo" message for that case.
        const u = getUser();
        if (!u) return; // anonymous → no modal
        const path = (window.location.pathname || "");
        if (path.startsWith("/s/")) return; // public Q&A surface → handled by PublicSilo
        if (path.startsWith("/fan-portal")) return; // fan portal is non-creator surface
        const detail = e.detail || {};
        setEvent(detail);
      } catch { /* swallow */ }
    };
    window.addEventListener("llm-guard-blocked", handler);
    return () => window.removeEventListener("llm-guard-blocked", handler);
  }, []);

  if (!event) return null;
  const provider = (event.provider || "gemini").toLowerCase();
  const provLabel = PROVIDER_LABEL[provider] || provider;
  const consoleUrl = event.console_url || "#";

  const goToByokSettings = () => {
    setEvent(null);
    nav("/dashboard/account-settings?focus=byok");
  };

  const onTopupSuccess = () => {
    setTopupOpen(false);
    setEvent(null);
  };

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" data-testid="llm-guard-modal">
        <div className="modal-safe bg-white rounded-lg max-w-lg w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between p-4 border-b hairline">
            <div className="flex items-center gap-2">
              <ShieldCheck size={18} weight="duotone" className="text-[#C16E5A]" />
              <div className="text-sm font-medium text-[#1C1B1A]">{provLabel} access locked</div>
            </div>
            <button onClick={() => setEvent(null)} className="text-[#6B6A68] hover:text-[#1C1B1A]" data-testid="llm-guard-close">
              <X size={18} weight="bold" />
            </button>
          </div>

          <div className="p-5 space-y-4">
            <div>
              <div className="font-serif-display text-xl text-[#1C1B1A] leading-tight mb-2">
                You're out of {provLabel} fuel
              </div>
              <p className="text-sm text-[#3A4A3E] leading-relaxed">
                You don't have a {provLabel} key set up, AND your {provLabel} voucher bucket is empty.
                Pick a path below and you're back in business in 30 seconds.
              </p>
            </div>

            <button
              onClick={() => setTopupOpen(true)}
              data-testid="llm-guard-topup"
              className="w-full bg-[#1C1B1A] text-[#FDFCF8] rounded-md p-3 hover:bg-[#3A4A3E] transition-colors flex items-center justify-between text-left"
            >
              <div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-[#FDFCF8]/70">Quick option</div>
                <div className="text-sm font-medium">Top up the {provLabel} bucket</div>
                <div className="text-[10px] text-[#FDFCF8]/70 mt-0.5">Pay 3× the raw {provLabel} rate. Funds split across your silos.</div>
              </div>
              <Plus size={16} weight="bold" />
            </button>

            <div className="text-[10px] uppercase tracking-[0.15em] text-[#6B6A68] text-center">— OR —</div>

            <button
              onClick={goToByokSettings}
              data-testid="llm-guard-byok"
              className="w-full bg-[#E8EBE5] text-[#1C1B1A] rounded-md p-3 hover:bg-[#D4DDD2] transition-colors flex items-center justify-between text-left"
            >
              <div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-[#2F6B3A]">Save 3×</div>
                <div className="text-sm font-medium">Paste your own {provLabel} key</div>
                <div className="text-[10px] text-[#6B6A68] mt-0.5">Bill {provLabel} directly. 1× cost. We send any stranded voucher to your withdraw balance.</div>
              </div>
              <Key size={16} weight="duotone" />
            </button>

            <a
              href={consoleUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="llm-guard-console-link"
              className="text-[11px] text-[#6B6A68] hover:text-[#1C1B1A] inline-flex items-center gap-1"
            >
              Don't have a key yet? Get one from {provLabel} <ArrowSquareOut size={10} />
            </a>

            <p className="text-[11px] text-[#6B6A68] leading-relaxed pt-1 border-t hairline mt-1" data-testid="llm-guard-topup-note">
              Heads up: <strong>"Top up the bucket"</strong> runs on the platform's shared AI key. If you
              top up and still get failures, the platform key itself may be temporarily down — that's not
              your fault and your funds are safe. <strong>Pasting your own key always works</strong> (and
              bills at 1×).
            </p>
          </div>
        </div>
      </div>

      <VoucherTopupModal
        open={topupOpen}
        onClose={() => setTopupOpen(false)}
        onSuccess={onTopupSuccess}
        defaultProvider={provider}
      />
    </>
  );
}
